import { useCallback, useEffect, useMemo, useState } from 'react';

import ReactSelect, { CSSObjectWithLabel, InputProps, MultiValue, SingleValue } from 'react-select';
import AsyncSelect from 'react-select/async';

import { MULTI_SELECT_STYLES } from '@/components/ui/multi-select';
import { DoubleClickInput, SELECT_COMPONENTS } from '@/components/ui/select';

export type DropdownOption = { label: string; value: string };

type DropdownBaseProps = {
  id: string;
  placeholder: string;
  multiple: boolean;
  options: DropdownOption[];
  // Names for picks that aren't in `options`, e.g. a saved value looked up by id. Shown, never offered in the menu.
  selectedOptions?: DropdownOption[];
  // Server search: when set, typing asks this instead of filtering `options`.
  loadOptions?: (search: string) => Promise<DropdownOption[]>;
  value: string | string[] | null | undefined;
  onChange: (value: string | string[] | null) => void;
  onBlur?: () => void;
  isLoading?: boolean;
  isError?: boolean;
  readonly?: boolean;
  invalid?: boolean;
};

function toValueArray(value: DropdownBaseProps['value']): string[] {
  if (Array.isArray(value)) {
    return value;
  }
  return value === undefined || value === null || value === '' ? [] : [value];
}

function isMultiValue(selected: MultiValue<DropdownOption> | SingleValue<DropdownOption>): selected is MultiValue<DropdownOption> {
  return Array.isArray(selected);
}

function useCloseMenuOnClickOutside(id: string, closeMenu: () => void) {
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target instanceof Element ? event.target : null;
      const className = target?.getAttribute('class') || '';
      if (className.includes(id)) {
        return;
      }
      closeMenu();
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [id, closeMenu]);
}

// react-select shows a failed search as an empty menu, the same as "no matches". Note the failure, then
// rethrow: react-select only caches results, so the same search is retried next time.
function useSearchFailure(loadOptions: DropdownBaseProps['loadOptions']) {
  const [isSearchError, setIsSearchError] = useState(false);

  const reportingLoadOptions = useMemo(
    () =>
      loadOptions &&
      (async (search: string) => {
        try {
          return await loadOptions(search);
        } catch (error) {
          setIsSearchError(true);
          throw error;
        }
      }),
    [loadOptions],
  );

  const onSearchChange = useCallback(() => setIsSearchError(false), []);

  return { isSearchError, reportingLoadOptions, onSearchChange };
}

export function DropdownBase({
  id,
  placeholder,
  multiple,
  options,
  selectedOptions = [],
  loadOptions,
  value,
  onChange,
  onBlur,
  isLoading,
  isError,
  readonly,
  invalid,
}: DropdownBaseProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const closeMenu = useCallback(() => setIsMenuOpen(false), []);
  useCloseMenuOnClickOutside(id, closeMenu);
  const { isSearchError, reportingLoadOptions, onSearchChange } = useSearchFailure(loadOptions);

  // Picks from server search aren't in `options`; remember their names so they still show once the search clears.
  const [searchPicks, setSearchPicks] = useState<DropdownOption[]>([]);

  // A saved value can outlive its option; show it as the pick, but keep it out of the menu so it can't be picked again.
  const currentPicks = toValueArray(value).map(
    (saved) =>
      options.find((option) => option.value === saved) ??
      selectedOptions.find((option) => option.value === saved) ??
      searchPicks.find((option) => option.value === saved) ?? { label: saved, value: saved },
  );

  const handleChange = (selected: MultiValue<DropdownOption> | SingleValue<DropdownOption>) => {
    if (loadOptions) {
      const picked = isMultiValue(selected) ? [...selected] : selected ? [selected] : [];
      setSearchPicks((remembered) => [...remembered.filter((option) => !picked.some((pick) => pick.value === option.value)), ...picked]);
    }
    if (isMultiValue(selected)) {
      onChange(selected.length > 0 ? selected.map((option) => option.value) : null);
    } else {
      onChange(selected?.value ?? null);
    }
  };

  const sharedProps = {
    components: {
      ...SELECT_COMPONENTS<DropdownOption>(),
      Input: (props: InputProps<DropdownOption, false>) => <DoubleClickInput {...props} setIsMenuOpen={setIsMenuOpen} />,
    },
    unstyled: true,
    blurInputOnSelect: true,
    isClearable: !readonly,
    value: multiple ? currentPicks : (currentPicks[0] ?? null),
    onChange: handleChange,
    onBlur,
    isDisabled: readonly,
    'aria-invalid': !!invalid,
    placeholder,
    menuIsOpen: isMenuOpen,
    // DoubleClickInput opens the menu on focus; React Select then treats Tab as select-and-preventDefault
    // while the menu is open, so Tab would pick the highlighted option. Let Tab bubble so it advances instead.
    tabSelectsValue: false,
    styles: {
      control: (state: CSSObjectWithLabel) => ({
        ...state,
        borderColor: isMenuOpen ? 'rgb(var(--default-blue))' : 'rgb(var(--grey-3))',
        opacity: '1',
        backgroundColor: readonly ? 'rgb(var(--grey-1))' : state.backgroundColor,
      }),
    },
    classNamePrefix: id,
    className: 'w-full',
    menuPortalTarget: document.body,
    isLoading,
    noOptionsMessage: isError || isSearchError ? () => "Couldn't load options. Please try again." : undefined,
  };

  if (loadOptions) {
    return (
      <AsyncSelect<DropdownOption>
        {...sharedProps}
        // @ts-expect-error - React Select has PITA types for this
        classNames={MULTI_SELECT_STYLES<DropdownOption>(!!invalid)}
        // @ts-expect-error - isMulti is set at runtime, but SELECT_COMPONENTS and DoubleClickInput are typed single-select
        isMulti={multiple}
        cacheOptions
        defaultOptions={options}
        loadOptions={reportingLoadOptions}
        onInputChange={onSearchChange}
      />
    );
  }

  return (
    <ReactSelect<DropdownOption>
      {...sharedProps}
      // @ts-expect-error - React Select has PITA types for this
      classNames={MULTI_SELECT_STYLES<DropdownOption>(!!invalid)}
      // @ts-expect-error - isMulti is set at runtime, but SELECT_COMPONENTS and DoubleClickInput are typed single-select
      isMulti={multiple}
      options={options}
    />
  );
}
